"use client";

import { LanguageSelector } from "@/components/ui/language-selector";
import { COLLECTION_NAMES, LANGUAGE_CODES } from "@/constants/collections";
import { useCart } from "@/contexts/cart-context";
import { CartStorage } from "@/libs/cart-storage";
import { cn } from "@/libs/utils";
import { KwikpassLogin } from "@/shopify/KwikPass/KwikpassLogin";
import { fetchShopifyCollectionByHandle } from "@/shopify/api/shopify-collection";
import type { IApiProduct } from "@/types/collection";
import { ChevronDown, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const Header = () => {
  const { openCart, totalItems } = useCart();
  const { t, i18n } = useTranslation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [products, setProducts] = useState<IApiProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [shopifyItemCount, setShopifyItemCount] = useState(0);

  useEffect(() => {
    const updateShopifyItemCount = () => {
      const savedItems = CartStorage.getShopifyCartItems();
      const itemCount = savedItems.reduce(
        (total: number, item: any) => total + item.quantity,
        0,
      );
      setShopifyItemCount(itemCount);
    };

    updateShopifyItemCount();

    const interval = setInterval(updateShopifyItemCount, 100);

    return () => clearInterval(interval);
  }, []);

  // New Shopify implementation
  useEffect(() => {
    const fetchCollectionProducts = async () => {
      try {
        setIsLoading(true);
        const collection =
          await fetchShopifyCollectionByHandle(COLLECTION_NAMES);

        if (
          collection?.products?.edges &&
          collection.products.edges.length > 0
        ) {
          const transformedProducts = collection.products.edges.map(
            ({ node }: any) => {
              // Find the Hindi title metafield if it exists
              const titleHiMetafield = node.metafields?.find(
                (metafield: any) =>
                  metafield?.namespace === "translations" &&
                  metafield?.key === "title_hi",
              );

              // Use Hindi title if language is Hindi and metafield exists, otherwise use English title
              const title =
                i18n.language === "hi" && titleHiMetafield
                  ? titleHiMetafield.value
                  : node.title;

              return {
                id: node.id,
                title: title,
                handle: node.handle,
              };
            },
          );

          setProducts(transformedProducts);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCollectionProducts();
  }, [i18n.language]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useLayoutEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  return (
    <header className="sticky top-0 z-50 shadow">
      {/* Navigation */}
      <nav className=" bg-[white] py-4 ">
        <div className="container-custom flex items-center justify-between   ">
          {/* Mobile menu button and language selector */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              id="header-menu"
              onClick={toggleMenu}
              data-test-id="mobile-menu-button"
              className="font-medium text-red-500 focus:outline-none"
              aria-label="Toggle menu"
            >
              {t("header.menu")}
            </button>
            <LanguageSelector />
          </div>

          {/* Desktop Navigation */}
          <ul className="hidden lg:flex items-center space-x-8 px-2">
            <li className="relative group" data-test-id="nav-shop">
              <Link
                aria-label="shop"
                href="#"
                data-test-id="shop-link"
                className="text-[#920000] font-semibold group-hover:text-[#cc0000] py-2  text-[22px] md:text-[28px] flex items-center gap-1 group transition-colors"
              >
                {t("header.shop")}
                <ChevronDown className="w-6 h-6 group-hover:rotate-180 transition-all duration-300" />
              </Link>
              <div className="absolute left-0 top-[calc(100%_-_2px)] hidden group-hover:block bg-white rounded shadow-md w-[300px] z-10">
                <ul className="py-2 max-h-[550px] overflow-y-auto scrollbar-thin">
                  {isLoading ? (
                    <li
                      className="px-4 py-2 text-gray-500"
                      data-test-id="loading-products"
                    >
                      {t("common.loading")}
                    </li>
                  ) : (
                    <>
                      {products.length > 0 ? (
                        products.map((product) => (
                          <li key={product.id}>
                            <Link
                              aria-label={product.title}
                              data-test-id={`product-item-${product.handle}`}
                              href={`/products/${product.handle}`}
                              className="block px-4 py-2 hover:bg-gray-50 capitalize"
                            >
                              {product.title}
                            </Link>
                          </li>
                        ))
                      ) : (
                        <li
                          className="px-4 py-2 text-gray-500"
                          data-test-id="no-products"
                        >
                          {t("common.noProductsFound")}
                        </li>
                      )}
                      <li className="border-t mt-2">
                        <Link
                          aria-label="View All Products"
                          data-test-id="view-all-products"
                          href={`/collections/${decodeURIComponent(
                            COLLECTION_NAMES,
                          )}`}
                          className="block px-4 py-2 hover:bg-gray-50 font-semibold text-[#920000] hover:text-[#cc0000]"
                        >
                          {t("common.viewAllProducts")}
                        </Link>
                      </li>
                      <li className="border-t mt-2">
                        <Link
                          aria-label="Build your Bundle"
                          data-test-id="build-your-bundle"
                          href={`/productbundle/${process.env.NEXT_PUBLIC_BUNDLE_ID}`}
                          className="block px-4 py-2 hover:bg-gray-50 font-semibold text-[#920000] hover:text-[#cc0000]"
                        >
                          {t("header.buildYourBundle")}
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </li>
            <li className="relative group" data-test-id="nav-learn">
              <Link
                aria-label="learn"
                href="#"
                data-test-id="learn-link"
                className="text-[#920000] font-semibold text-[22px] md:text-[28px] group-hover:text-[#cc0000] py-2  flex items-center gap-1 group transition-colors"
              >
                {t("header.learn")}
                <ChevronDown className="w-5 h-5 group-hover:rotate-180 transition-all duration-300" />
              </Link>
              <div className="absolute left-0 top-12 mt-1 hidden group-hover:block bg-white rounded shadow-md w-40 z-10">
                <ul className="py-2">
                  <li>
                    <Link
                      aria-label="NRT"
                      href="/blogs/nrt"
                      data-test-id="learn-link-nrt"
                      className="block px-4 py-2 hover:bg-gray-50"
                    >
                      {t("navigation.nrt")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      aria-label="quitChewing"
                      href="/blogs/quit-chewing"
                      data-test-id="learn-link-quit-chewing"
                      className="block px-4 py-2 hover:bg-gray-50"
                    >
                      {t("navigation.quitChewing")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      aria-label="quitSmoking"
                      href="/blogs/quit-smoking"
                      data-test-id="learn-link-quit-smoking"
                      className="block px-4 py-2 hover:bg-gray-50"
                    >
                      {t("navigation.quitSmoking")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      aria-label="healthyLiving"
                      href="/blogs/health"
                      data-test-id="learn-link-healthy-living"
                      className="block px-4 py-2 hover:bg-gray-50"
                    >
                      {t("navigation.healthyLiving")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      aria-label="ryzeGums"
                      href="/blogs/ryze-gum"
                      data-test-id="learn-link-ryze-gums"
                      className="block px-4 py-2 hover:bg-gray-50"
                    >
                      {t("navigation.ryzeGums")}
                    </Link>
                  </li>
                </ul>
              </div>
            </li>
            <li>
              <Link
                aria-label="Science"
                href="/pages/science"
                data-test-id="science-link"
                className="text-[#920000] font-semibold hover:text-[#cc0000] text-[22px] md:text-[28px] transition-colors"
              >
                {t("header.science")}
              </Link>
            </li>
          </ul>

          {/* Logo */}
          <Link
            aria-label="logo"
            href="/"
            data-test-id="logo-link"
            className="absolute left-1/2 -translate-x-1/2 flex items-center"
          >
            <div className=" w-[120px] h-[60px]  md:w-[200px] md:h-[80px] relative">
              <Image
                src="/logo.png"
                alt="Atom Health"
                fill
                style={{ objectFit: "contain" }}
                priority
                data-test-id="logo-image"
              />
            </div>
          </Link>

          {/* Right side icons */}
          <div className="flex items-center gap-x-4 px-2">
            {/* Desktop Language Selector - hidden on mobile */}
            <div className="hidden lg:block">
              <LanguageSelector />
            </div>
            <div className="relative">
              <button
                id="cart-svg"
                aria-label="cart-svg"
                onClick={() => openCart()}
                className="text-secondary hover:text-secondary/80 relative"
                data-test-id="cart-button"
              >
                <Image
                  src="/images/add-to-cart.svg"
                  alt="Cart"
                  width={25}
                  height={25}
                  className="w-[25px] h-[25px] md:w-[34px] md:h-[34px] opacity-80 hover:opacity-100"
                />
                {shopifyItemCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-[4px] w-3 h-3 rounded-full bg-[#f0535f] border border-white"
                    data-test-id="cart-indicator"
                  />
                )}
              </button>
            </div>
            <KwikpassLogin />
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 w-full h-full z-40 bg-black bg-opacity-50 transition-opacity "
          onClick={toggleMenu}
          data-test-id="mobile-menu-overlay"
        />
      )}
      <div
        className={cn(
          "fixed inset-y-0 left-0 w-[80%] bg-white z-50 transform transition-transform duration-300 ease-in-out",
          isMenuOpen ? "translate-x-0" : "-translate-x-full",
        )}
        data-test-id="mobile-sidebar"
      >
        <div className="flex justify-end p-4 pb-2">
          <button
            id="close-x"
            aria-label="close-x"
            onClick={toggleMenu}
            className="text-secondary focus:outline-none"
            data-test-id="close-mobile-menu"
          >
            <X size={24} />
          </button>
        </div>
        <div className="px-4 pt-2 pb-20 max-h-[calc(100vh-4rem)] overflow-y-auto scrollbar-hide">
          <ul className="space-y-4">
            <li>
              <p className="font-medium text-secondary mb-2 underline decoration-red-500 decoration-2">
                {t("header.shop")}
              </p>
              <ul className="space-y-2">
                {isLoading ? (
                  <li
                    className="text-gray-500"
                    data-test-id="mobile-loading-products"
                  >
                    {t("common.loading")}
                  </li>
                ) : (
                  <>
                    {products.length > 0 ? (
                      products.map((product) => (
                        <li key={product.id}>
                          <Link
                            aria-label={product.title}
                            href={`/products/${product.handle}`}
                            onClick={toggleMenu}
                            data-test-id={`mobile-product-item-${product.handle}`}
                            className="block font-medium text-secondary/80 hover:text-secondary capitalize"
                          >
                            {product.title}
                          </Link>
                        </li>
                      ))
                    ) : (
                      <li
                        className="text-gray-500"
                        data-test-id="mobile-no-products"
                      >
                        {t("common.noProductsFound")}
                      </li>
                    )}
                    <li className="border-t mt-2 pt-2">
                      <Link
                        aria-label="View All Products"
                        data-test-id="mobile-view-all-products"
                        href={`/collections/${decodeURIComponent(
                          COLLECTION_NAMES,
                        )}`}
                        onClick={toggleMenu}
                        className="block font-medium text-[#920000] hover:text-[#cc0000]"
                      >
                        {t("common.viewAllProducts")}
                      </Link>
                    </li>
                    <li className="border-t mt-2 pt-2 pb-2 border-b border-gray-200">
                      <Link
                        aria-label="Build your Bundle"
                        data-test-id="mobile-build-your-bundle"
                        href={`/productbundle/${process.env.NEXT_PUBLIC_BUNDLE_ID}`}
                        onClick={toggleMenu}
                        className="block font-medium text-[#920000] hover:text-[#cc0000]"
                      >
                        {t("header.buildYourBundle")}
                      </Link>
                    </li>
                  </>
                )}
              </ul>
            </li>
            <li className="">
              <p className="font-medium text-secondary mb-2 underline decoration-red-500 decoration-2">
                {t("header.learn")}
              </p>
              <ul className="space-y-2">
                <li>
                  <Link
                    aria-label="nrt"
                    href="/blogs/nrt"
                    onClick={toggleMenu}
                    className="block font-medium text-secondary/80 hover:text-secondary"
                    data-test-id="mobile-learn-link-nrt"
                  >
                    {t("navigation.nrt")}
                  </Link>
                </li>
                <li>
                  <Link
                    aria-label="quitChewing"
                    href="/blogs/quit-chewing"
                    onClick={toggleMenu}
                    className="block font-medium text-secondary/80 hover:text-secondary"
                    data-test-id="mobile-learn-link-quit-chewing"
                  >
                    {t("navigation.quitChewing")}
                  </Link>
                </li>
                <li>
                  <Link
                    aria-label="quitSmoking"
                    href="/blogs/quit-smoking"
                    onClick={toggleMenu}
                    className="block font-medium text-secondary/80 hover:text-secondary"
                    data-test-id="mobile-learn-link-quit-smoking"
                  >
                    {t("navigation.quitSmoking")}
                  </Link>
                </li>
                <li>
                  <Link
                    aria-label="health"
                    href="/blogs/health"
                    onClick={toggleMenu}
                    className="block font-medium text-secondary/80 hover:text-secondary"
                    data-test-id="mobile-learn-healthy-living"
                  >
                    {t("navigation.healthyLiving")}
                  </Link>
                </li>
                <li>
                  <Link
                    aria-label="ryze-gum"
                    href="/blogs/ryze-gum"
                    onClick={toggleMenu}
                    className="block font-medium text-secondary/80 hover:text-secondary"
                    data-test-id="mobile-learn-ryze-gums"
                  >
                    {t("navigation.ryzeGums")}
                  </Link>
                </li>
              </ul>
            </li>
            <li className="border-t border-gray-200 pt-4">
              <Link
                aria-label="science"
                href="/pages/science"
                onClick={toggleMenu}
                className="block font-medium text-secondary hover:text-secondary/80 underline decoration-red-500 decoration-2"
                data-test-id="mobile-science-link"
              >
                {t("header.science")}
              </Link>
            </li>
            {/* Removed language selector from mobile menu since it's now in header */}
          </ul>
          <div
            className=" w-full flex justify-center space-x-4"
            data-test-id="mobile-social-links"
          >
            <a
              href="https://x.com/Atomhealth_in"
              target="_blank"
              rel="noopener noreferrer"
              data-test-id="twitter-link"
              className="text-secondary hover:text-secondary/80"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.933ZM17.61 20.644h2.039L6.486 3.24H4.298L17.61 20.644Z"
                  fill="currentColor"
                />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/atomhealth.in/"
              target="_blank"
              rel="noopener noreferrer"
              data-test-id="instagram-link"
              className="text-secondary hover:text-secondary/80"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"
                  fill="currentColor"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
